// ═══════════════════════════════════════════════════════════
// Mantine Hooks Shim — drop-in replacements for @mantine/hooks
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';

// ── useDisclosure ──────────────────────────────────────────

interface DisclosureHandlers {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export function useDisclosure(
  initial = false,
  callbacks?: { onOpen?: () => void; onClose?: () => void }
): [boolean, DisclosureHandlers] {
  const [opened, setOpened] = useState(initial);
  const open = useCallback(() => {
    setOpened(o => { if (!o) { callbacks?.onOpen?.(); return true; } return o; });
  }, [callbacks]);
  const close = useCallback(() => {
    setOpened(o => { if (o) { callbacks?.onClose?.(); return false; } return o; });
  }, [callbacks]);
  const toggle = useCallback(() => {
    setOpened(o => { if (o) { callbacks?.onClose?.(); return false; } else { callbacks?.onOpen?.(); return true; } });
  }, [callbacks]);
  return [opened, { open, close, toggle }];
}

// ── useDebouncedValue ──────────────────────────────────────

export function useDebouncedValue<T>(value: T, wait: number, options?: { leading?: boolean }): [T, () => void] {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (options?.leading && timerRef.current === null) {
      setDebounced(value);
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!options?.leading) setDebounced(value);
      timerRef.current = null;
    }, wait);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, wait, options?.leading]);

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }, []);

  return [debounced, cancel];
}

// ── useDebouncedCallback ───────────────────────────────────

export function useDebouncedCallback<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const debounced = useCallback((...args: Parameters<T>) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { fnRef.current(...args); }, delay);
  }, [delay]);
  return debounced as unknown as T;
}

// ── useMediaQuery ──────────────────────────────────────────

export function useMediaQuery(query: string, _initialValue?: boolean): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return _initialValue ?? false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

// ── useLocalStorage ────────────────────────────────────────

export function useLocalStorage<T>({ key, defaultValue, serialize = JSON.stringify, deserialize = JSON.parse }: {
  key: string;
  defaultValue: T;
  serialize?: (v: T) => string;
  deserialize?: (s: string) => T;
}): [T, (val: T | ((prev: T) => T)) => void, () => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? deserialize(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback((val: T | ((prev: T) => T)) => {
    setValue(prev => {
      const next = typeof val === 'function' ? (val as (p: T) => T)(prev) : val;
      try { localStorage.setItem(key, serialize(next)); } catch {}
      return next;
    });
  }, [key, serialize]);

  const remove = useCallback(() => {
    try { localStorage.removeItem(key); } catch {}
    setValue(defaultValue);
  }, [key, defaultValue]);

  return [value, set, remove];
}

// ── useSessionStorage ──────────────────────────────────────

export function useSessionStorage<T>({ key, defaultValue }: { key: string; defaultValue: T }): [T, (val: T | ((prev: T) => T)) => void, () => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch { return defaultValue; }
  });
  const set = useCallback((val: T | ((prev: T) => T)) => {
    setValue(prev => {
      const next = typeof val === 'function' ? (val as (p: T) => T)(prev) : val;
      try { sessionStorage.setItem(key, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [key]);
  const remove = useCallback(() => { try { sessionStorage.removeItem(key); } catch {}; setValue(defaultValue); }, [key, defaultValue]);
  return [value, set, remove];
}

// ── useClipboard ───────────────────────────────────────────

export function useClipboard({ timeout = 2000 } = {}): { copy: (val: string) => void; copied: boolean; reset: () => void; error: Error | null } {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const copy = useCallback((val: string) => {
    navigator.clipboard.writeText(val)
      .then(() => { setCopied(true); setError(null); setTimeout(() => setCopied(false), timeout); })
      .catch(e => setError(e));
  }, [timeout]);
  const reset = useCallback(() => { setCopied(false); setError(null); }, []);
  return { copy, copied, error, reset };
}

// ── useCounter ─────────────────────────────────────────────

export function useCounter(initial = 0, { min, max, clamp }: { min?: number; max?: number; clamp?: boolean } = {}) {
  const [count, setCount] = useState(initial);
  const _clamp = (v: number) => {
    if (min !== undefined && v < min) return clamp ? min : v;
    if (max !== undefined && v > max) return clamp ? max : v;
    return v;
  };
  return [count, {
    increment: () => setCount(c => _clamp(c + 1)),
    decrement: () => setCount(c => _clamp(c - 1)),
    set: (v: number) => setCount(_clamp(v)),
    reset: () => setCount(initial),
  }] as const;
}

// ── useToggle ─────────────────────────────────────────────

export function useToggle<T = boolean>(options?: readonly T[]): [T, (val?: T) => void] {
  const vals = options ?? ([false, true] as unknown as T[]);
  const [idx, setIdx] = useState(0);
  const toggle = (val?: T) => {
    if (val !== undefined) {
      const i = vals.indexOf(val);
      if (i !== -1) setIdx(i);
    } else {
      setIdx(i => (i + 1) % vals.length);
    }
  };
  return [vals[idx], toggle];
}

// ── useHover ──────────────────────────────────────────────

export function useHover<T extends HTMLElement = HTMLElement>(): { hovered: boolean; ref: React.RefObject<T> } {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onEnter = () => setHovered(true);
    const onLeave = () => setHovered(false);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    return () => { el.removeEventListener('mouseenter', onEnter); el.removeEventListener('mouseleave', onLeave); };
  }, []);
  return { hovered, ref };
}

// ── useWindowScroll ─────────────────────────────────────────

export function useWindowScroll(): [{ x: number; y: number }, (pos: { x: number; y: number }) => void] {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handler = () => setPos({ x: window.scrollX, y: window.scrollY });
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  const scrollTo = useCallback(({ x, y }: { x: number; y: number }) => window.scrollTo(x, y), []);
  return [pos, scrollTo];
}

// Need React for refs in hooks
import type React from 'react';
